import type { ViewComponent } from "@frui.ts/views";
import React from "react";
import { registerView } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import { Row, Col, Form } from "react-bootstrap";
import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import BalanceDetailViewModel from "viewModels/finance/accountingInventory/balance/balanceDetailViewModel";
import AccountBalanceCheckRequestStatus from "entities/accountBalanceCheckRequestStatus";
import FakeInput from "@edaz/edazkit/controls/FakeInput";
import { emptyValue } from "../../../../utils/emptyValue";
import BalanceStatusSuccessIcon from "./components/BalanceStatusSuccessIcon";
import BalanceStatusErrorIcon from "./components/BalanceStatusErrorIcon";

const BalanceDetailView: ViewComponent<BalanceDetailViewModel> = observer(({ vm }) => {
  const renderStatusIcon = (status: AccountBalanceCheckRequestStatus) => {
    switch (status) {
      case AccountBalanceCheckRequestStatus.Processed:
        return <BalanceStatusSuccessIcon />;
      case AccountBalanceCheckRequestStatus.ProcessingFailed:
        return <BalanceStatusErrorIcon />;
      case AccountBalanceCheckRequestStatus.ToBeProcessed:
        return null;
      default:
        return null;
    }
  };

  return (
    <LoaderOverlay loading={vm.busyWatcher.isBusy}>
      <h3 className="m-0 pb-7 text-left">{vm.translate("title")}</h3>
      <Form>
        {/* Období sekce */}
        <div>
          <div className="bg-secondary px-4 px-lg-5 py-3 rounded-top text-white">
            <span className="font-size-lg font-weight-bold">{vm.translate("periodSection")}</span>
          </div>
          <div className="bg-light mb-4 px-4 px-lg-5 pt-4 pb-2 rounded-bottom">
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{vm.translate("accountingPeriodTo")}</Form.Label>
                  <FakeInput
                    target={vm.item}
                    property="accountingPeriodTo"
                    valueFormatter={value => (!value ? emptyValue : `${value.split("-")[1]}/${value.split("-")[0]}`)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        {/* Kontrola sekce */}
        <div>
          <div className="bg-secondary px-4 px-lg-5 py-3 rounded-top text-white">
            <span className="font-size-lg font-weight-bold">{vm.translate("controlSection")}</span>
          </div>
          <div className="bg-light mb-4 px-4 px-lg-5 pt-4 pb-2 rounded-bottom">
            <Row>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{vm.translate("status")}</Form.Label>
                  <div className="position-relative">
                    <FakeInput
                      target={vm.item}
                      className={vm.getStatusTextClass(vm.item?.status)}
                      property="status"
                      valueFormatter={value => (!value ? emptyValue : vm.translateStatus(value))}
                    />
                    {vm.item?.status && (
                      <div className="position-absolute" style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}>
                        {renderStatusIcon(vm.item.status)}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </div>
        </div>

        {/* Tlačítka */}
        <Row lg={12} xl={12}>
          <div className="col-md-6 col-sm-6">
            <Button size="sm" type="reset" style={{ borderRadius: "0.25rem" }} variant="light" onClick={vm.onClose}>
              {vm.translateGeneral("back")}
            </Button>
          </div>
          <div className="col-md-6 col-sm-6">
            <div className="d-flex justify-content-end">
              <Button
                iconLeft={IconType.Download}
                size="sm"
                style={{ borderRadius: "0.25rem" }}
                variant="success"
                onClick={vm.onDownloadResult}
                disabled={vm.item?.status !== AccountBalanceCheckRequestStatus.Processed}>
                {vm.translate("downloadResult")}
              </Button>
            </div>
          </div>
        </Row>
      </Form>
    </LoaderOverlay>
  );
});

registerView(BalanceDetailView, BalanceDetailViewModel);

export default BalanceDetailView;
